import { usePrivateToast } from "@/shared/usePrivateToast";
import { usePrivateApi } from "@/shared/usePrivateApi";
import { useState } from "react";
import { Button, FormField, Modal, NumberInput, TextInput } from "@a2z/ui";
import { useQueryClient } from "@tanstack/react-query";

import { endpoints } from "@/lib/api/endpoints";
import { useTranslate } from "@/lib/i18n";
import { queryKeys, useApiQuery } from "@/shared/queries";
import { useErrorMessage } from "@/shared/errors";
import type { DrawersView, ShiftView } from "@/lib/api/types";
import {
  UnsavedChangesPrompt,
  useDiscardGuard,
} from "@/shared/UnsavedChangesPrompt";

/**
 * OPENING A DRAWER WITHOUT LEAVING THE TILL (Unit C, "soft block + one-tap open").
 *
 * The founder's choice over the two alternatives — block every action until a
 * drawer is open, or carry on and let the refusal happen at settle time — and the
 * persona review's unanimous reason for rejecting the hard block is the one this
 * dialog exists to serve: blocking pushes service OFF the system. A cashier who
 * cannot ring a bill rings it on paper, and a meal taken on paper is invisible.
 *
 * So the till never stops working, and the way out is always one tap away. Before
 * this, the way out was a LINK to `/manage/shift`: the cashier with a customer at
 * the counter and a half-finished bill on screen had to navigate off the bill,
 * open a drawer in the back office, and find their way back. In practice nobody
 * does that mid-service — they take the cash and put it in the till, which is the
 * untracked payment D25 exists to prevent, caused by the screen meant to prevent it.
 *
 * `elevated`, because this opens OVER the settle sheet: a cashier meeting the
 * warning at the moment of taking money must be able to fix it there, not lose the
 * bill to fix it elsewhere.
 */
export function OpenDrawerDialog({
  isOpen,
  onClose,
  onOpened,
  elevated = false,
}: {
  isOpen: boolean;
  onClose: () => void;
  /** Fired after the drawer is open, for a caller that was mid-flow. */
  onOpened?: () => void;
  elevated?: boolean;
}) {
  const { apiFetch } = usePrivateApi();
  const toast = usePrivateToast();
  const t = useTranslate();
  const errorMessage = useErrorMessage();
  const queryClient = useQueryClient();
  const [label, setLabel] = useState("");
  /*
   * UX-round D23 — EMPTY, NOT "0".
   * «UX-round Dn» = the 2026-09-17 UX round's own numbering (`.roundtable/2026-09-17-restaurant-ux/decisions.md`), not §11's.
   *
   * A pre-filled zero is a figure nobody stated, sitting in the field whose
   * own hint says "count it before you start — this is what tonight's variance
   * is measured from". A cashier who taps past it has told the system the till
   * was empty; the variance at close is then wrong by the float and reads as
   * money missing. Empty is the honest opening state, and the confirm says so
   * rather than going quietly grey.
   *
   * The `—` this does NOT produce is the point of R14: the field is required,
   * so an uncounted drawer cannot be opened at all. Nothing downstream ever
   * has to render "not counted" for an open drawer.
   */
  const [openingFloat, setOpeningFloat] = useState("");
  const [isSubmitting, setSubmitting] = useState(false);

  /*
   * The store's boxes and who is on them (D95). Read only while the dialog is
   * OPEN: this sits on the till's highest-traffic screens, and a drawer list
   * fetched on every invoice render would be a request per bill for an answer
   * nobody asked for.
   */
  const drawersQuery = useApiQuery<DrawersView>(
    queryKeys.shiftDrawers,
    endpoints.restaurant.shiftDrawers,
    { enabled: isOpen },
  );
  const openDrawers = drawersQuery.data?.open ?? [];

  const floatValue = Number(openingFloat);
  const floatValid =
    openingFloat !== "" && Number.isFinite(floatValue) && floatValue >= 0;
  const labelValue = label.trim();
  const labelValid = labelValue.length > 0 && labelValue.length <= 40;
  /*
   * Somebody is already on that box. Caught here so the cashier is told BEFORE
   * the tap and pointed at the drawer screen, where the handover lives — the
   * server refuses it too (this list can be seconds stale) and must.
   */
  const taken = openDrawers.find(
    (drawer) => drawer.label.toLowerCase() === labelValue.toLowerCase(),
  );

  const submit = async () => {
    setSubmitting(true);
    try {
      await apiFetch<ShiftView>(endpoints.restaurant.shiftOpen, {
        method: "POST",
        body: { openingFloat: floatValue, label: labelValue },
      });
      await queryClient.invalidateQueries({ queryKey: queryKeys.shiftCurrent });
      await queryClient.invalidateQueries({ queryKey: queryKeys.shiftDrawers });
      await queryClient.invalidateQueries({ queryKey: queryKeys.shiftSummary });
      await queryClient.invalidateQueries({ queryKey: ["restaurant", "day-close"] });
      toast.success(t((m) => m.pos.drawerOpenedToast));
      setLabel("");
      setOpeningFloat("");
      onOpened?.();
      onClose();
    } catch (failure) {
      toast.error(errorMessage(failure));
    } finally {
      setSubmitting(false);
    }
  };

  const ready = labelValid && floatValid && taken === undefined;
  /* R8 — WHY the confirm will not fire, said before the press and not after
     it. `taken` already has its own paragraph on the canvas, so the footer
     names the two the cashier can fix by typing. */
  const refusal = !labelValid
    ? t((m) => m.pos.openDrawerNeedsLabel)
    : !floatValid
      ? t((m) => m.pos.openDrawerNeedsFloat)
      : undefined;

  const guard = useDiscardGuard(
    label.trim() !== "" || openingFloat !== "",
    onClose,
  );

  return (
    <>
    <Modal
      isOpen={isOpen}
      onClose={guard.requestClose}
      size="sm"
      elevated={elevated}
      title={t((m) => m.pos.openDrawerTitle)}
      closeLabel={t((m) => m.common.close)}
      data-testid="open-drawer-dialog"
      footerLeft={ready ? undefined : refusal}
      /* `outline`, not `secondary`, and the reason is the GROUND this dialog
         sits on (round-table finding 7). Over the manage surface the two
         variants read as primary and secondary the way they should; over the
         POS floor's dark theme `secondary` is a solid white fill, so a cancel
         button outweighed a primary that is greyed until the box is named —
         the screen was telling a cashier that backing out was the main thing
         to do here. `outline` carries the same job at the weight it deserves
         on both grounds. */
      footerActions={
        <>
          <Button variant="outline" size="md" onClick={guard.requestClose}>
            {t((m) => m.common.cancel)}
          </Button>
          <Button
            variant="primary"
            size="md"
            /* UX-round D23 / R8 — `aria-disabled`, not `disabled`: the press is taken
               and answered. The reason is already in `footerLeft`, so the
               button is never a silent grey rectangle. */
            aria-disabled={!ready || undefined}
            className={ready ? undefined : "opacity-[0.55]"}
            isLoading={isSubmitting}
            onClick={() => {
              if (!ready) return;
              void submit();
            }}
            data-testid="open-drawer-confirm"
          >
            {t((m) => m.pos.openDrawerConfirm)}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <p className="text-[length:var(--font-size-sm)] leading-relaxed text-[color:var(--text-muted)]">
          {t((m) => m.pos.openDrawerBody)}
        </p>
        <FormField
          name="drawerLabel"
          label={t((m) => m.pos.openDrawerLabelLabel)}
          helperText={t((m) => m.pos.openDrawerLabelHint)}
        >
          <TextInput
            name="drawerLabel"
            value={label}
            maxLength={40}
            onChange={(event) => setLabel(event.target.value)}
            hasError={label !== "" && !labelValid}
            placeholder={t((m) => m.pos.openDrawerLabelPlaceholder)}
            data-testid="open-drawer-label"
          />
        </FormField>
        {/* The store's own box names, as taps. A required free-text field is a
            typing exercise every night otherwise — and a typo quietly creates a
            box nobody can hand over. */}
        {drawersQuery.data && drawersQuery.data.knownLabels.length > 0 ? (
          <div
            className="flex flex-wrap gap-1.5"
            data-testid="open-drawer-suggestions"
          >
            {drawersQuery.data.knownLabels.map((known) => (
              <Button
                key={known}
                variant="ghost"
                size="sm"
                onClick={() => setLabel(known)}
                data-testid={`open-drawer-suggestion-${known}`}
              >
                {known}
              </Button>
            ))}
          </div>
        ) : null}
        <FormField
          name="openingFloat"
          label={t((m) => m.pos.openDrawerFloatLabel)}
          helperText={t((m) => m.pos.openDrawerFloatHint)}
        >
          <NumberInput
            name="openingFloat"
            allowDecimal
            min={0}
            value={openingFloat}
            onChange={(event) => setOpeningFloat(event.target.value)}
            hasError={openingFloat !== "" && !floatValid}
            data-testid="open-drawer-float"
          />
        </FormField>
        {taken ? (
          <p
            className="rounded-[var(--radius-list)] bg-[color:var(--warning-100,var(--surface-alt))] px-3 py-2 text-xs font-bold text-[color:var(--warning)]"
            data-testid="open-drawer-taken"
          >
            {t((m) => m.pos.openDrawerTaken, {
              label: taken.label,
              name: taken.openedBy ?? t((m) => m.pos.openDrawerSomeoneElse),
            })}
          </p>
        ) : null}
      </div>
    </Modal>
    <UnsavedChangesPrompt
      isOpen={guard.asking}
      onDiscard={guard.discard}
      onKeepEditing={guard.keepEditing}
      testId="open-drawer-discard-prompt"
    />
    </>
  );
}
